<template>
  <button
    :id="switchWrapperId"
    class="sam-toggle"
    type="button"
    :disabled="disabled"
    :class="`is-${switchStyle}`"
    data-samora
    @mousedown.prevent
    @keyup.enter="handleKeyup"
    @keyup.space="handleKeyup"
  >
    <label
      v-if="showBothLabels"
      class="sam-toggle__label mr-2"
      :class="{ active: !value }"
      :for="switchToggleId"
    >
      <span class="sam-toggle__switch-label">{{ labels[0] }}</span>
    </label>

    <input
      :id="switchToggleId"
      ref="checkboxElem"
      v-model="value"
      type="checkbox"
      class="sam-toggle__input"
      :disabled="disabled"
      @change="toggleSwitch"
    />

    <label
      class="sam-toggle__label"
      :class="{ active: value }"
      :for="switchToggleId"
    >
      <i class="mr-2">
        <span
          v-if="switchIcon"
          class="sam-toggle__before"
          v-html="require(`../../../icons/${switchIcon}.svg`)"
        />
        <span
          v-else
          class="sam-toggle__before"
          v-html="switchText"
        />
      </i>
      <span>
        <span class="sam-toggle__switch-label">{{ switchLabel }}</span>
        <span
          v-if="description"
          class="sam-toggle__switch-description"
        >
          {{ description }}
        </span>
      </span>
    </label>

    <SamTooltip
      v-if="tooltip"
      :anchor-name="switchWrapperId"
      v-bind="tooltip"
    />
  </button>
</template>

<script>
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import { randomizeId } from '../../../utils/utils';
import SamTooltip from '../SamTooltip/SamTooltip.vue';

export const TOGGLE_STYLES = {
  PRIMARY: SAMORA_STYLES.PRIMARY,
  SUCCESS: SAMORA_STYLES.SUCCESS,
  DEFAULT: SAMORA_STYLES.DEFAULT,
};

export default {
  name: 'SamToggle',
  components: { SamTooltip },
  model: {
    prop: 'data',
    event: 'change',
  },
  props: {
    /**
     * The labels to be used on selected and not selected states
     */
    labels: {
      type: Array,
      required: false,
      default: () => ['Off', 'On'],
    },
    /**
     * The description of the toggle which will be displayed beneath the label
     */
    description: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The SwitchToggle data variable
     * @values true, false
     */
    data: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the SwitchToggle is disabled (html attribute disabled)
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The SwitchToggle icon when [not-active, active]
     */
    icons: {
      type: Array,
      required: false,
      default: () => ['', 'interface-confirm--filled'],
    },
    /**
     * The SwitchToggle label when [not-active, active] if an icon is not set
     */
    inputLabels: {
      type: Array,
      required: false,
      default: () => ['', ''],
    },
    /**
     * The SwitchToggle style when [not-active, active]
     */
    samStyles: {
      type: Array,
      required: false,
      default: () => [TOGGLE_STYLES.DEFAULT, TOGGLE_STYLES.PRIMARY],
      validator: (val) => val.some((v) => Object.values(TOGGLE_STYLES).includes(v)),
    },
    /**
     * Whether to always show both labels in the toggle
     * @values true, false
     */
    alwaysShowLabels: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * SamToggle tooltip bind attrs.
     * If set a tooltip will be displayed with the attrs set in `tooltip`
     */
    tooltip: {
      type: Object,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      value: false,
      switchWrapperId: randomizeId('switchtogglewrapperid'),
      switchToggleId: randomizeId('switchtoggleid'),
      switchLabel: this.labels[0],
      switchStyle: this.samStyles[0],
      switchIcon: this.icons[0],
      switchText: this.inputLabels[0],
    };
  },
  computed: {
    showBothLabels() {
      return this.alwaysShowLabels !== false;
    },
  },
  watch: {
    data: {
      handler(newVal) {
        this.value = newVal;
        this.updateSwitchLabel();
        this.updateSwitchStyle();
      },
      immediate: true,
    },
    showBothLabels: {
      handler(newVal) {
        if (newVal) {
          // eslint-disable-next-line prefer-destructuring
          this.switchLabel = this.labels[1];
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleKeyup() {
      this.$refs.checkboxElem.click();
    },
    toggleSwitch() {
      this.updateSwitchLabel();
      this.updateSwitchStyle();
      /**
       * Triggers when SwitchToggle changes it's value
       * @event change
       * @property {boolean, string} value new value set
       */
      this.$emit('change', this.value);
    },
    updateSwitchLabel() {
      if (this.showBothLabels) return;
      const [falseLabel, trueLabel] = this.labels;

      if (this.value) {
        this.switchLabel = trueLabel;
      } else {
        this.switchLabel = falseLabel;
      }
    },
    updateSwitchStyle() {
      const [falseIcon, trueIcon] = this.icons;
      const [falseSamStyle, trueSamStyle] = this.samStyles;
      const [falseInputLabel, trueInputLabel] = this.inputLabels;

      if (this.value) {
        this.switchIcon = trueIcon;
        this.switchStyle = trueSamStyle;
        this.switchText = trueInputLabel;
      } else {
        this.switchIcon = falseIcon;
        this.switchStyle = falseSamStyle;
        this.switchText = falseInputLabel;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-toggle {
  @extend .m-0, .p-0, .items-center;
  height: 50px;
  border: none;
  outline: none;
  background-color: transparent;
  width: 100%;
  display: inline-flex;

  span {
    color: $neutral--90;
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &:disabled .sam-toggle__label {
    cursor: not-allowed;
    opacity: 0.65;
  }

  &__input {
    width: 0;
    height: 0;
    visibility: hidden;
    opacity: 0;
    position: absolute;
  }

  &__label {
    @extend .d-flex, .items-center;
    cursor: pointer;

    i {
      width: $size--9;
      height: $size--5;
      border-radius: $size--2-half;
      position: relative;
      transition: background-color $default-time $default-easing;

      .sam-toggle__before {
        position: absolute;
        top: $size--0--fourth;
        left: $size--0--fourth;
        width: $size--4;
        height: $size--4;
        border-radius: $size--4;
        display: block;
        background-color: $pure--white;
        transition: all $default-time $default-easing;

        ::v-deep(svg) {
          margin: $size--0--fourth;
          width: $size--3-half;
          height: $size--3-half;
        }
      }
    }
  }

  .sam-toggle__switch-label {
    @extend .text--body-2, .d-block;
  }

  .sam-toggle__switch-description {
    @extend .text--caption, .d-block, .text--light;
  }

  .sam-toggle__label.active {
    i {
      .sam-toggle__before {
        transform: translateX($size--4);
      }
    }

    .sam-toggle__switch-label {
      @extend .text--body-2--medium;
    }
  }

  /* Colors */
  &.is-primary {
    &:focus {
      span {
        color: $accent--unbabel-blue;
      }
      i {
        border-color: $informative--border;
        box-shadow: 0 0 0 $size--0-half $informative--border;
      }
    }

    i {
      background-color: $accent--unbabel-blue;
      border: 1px solid $accent--unbabel-blue;

      .sam-toggle__before {
        ::v-deep(svg) {
          color: $accent--unbabel-blue;
        }
      }
    }
  }

  &,
  &.is-default {
    &:focus {
      span {
        color: $neutral--80;
      }

      i {
        border-color: $informative--border;
        box-shadow: 0 0 0 $size--0-half $informative--border;
        .sam-toggle__before {
          ::v-deep(svg) {
            color: $accent--unbabel-blue;
          }
        }
      }
    }

    i {
      background-color: $neutral--60;
      border: 1px solid $neutral--60;

      .sam-toggle__before {
        ::v-deep(svg) {
          color: $neutral--60;
        }
      }
    }
  }

  &.is-success {
    &:focus {
      span {
        color: $success--main;
      }
      i {
        border-color: $neutral--60;
        box-shadow: 0 0 0 $size--0-half $neutral--60;
      }
    }

    i {
      background-color: $success--main;
      border: 1px solid $success--main;

      .sam-toggle__before {
        ::v-deep(svg) {
          color: $success--main;
        }
      }
    }
  }
}
</style>
